import React, { useRef } from 'react';
import algoliasearch from 'algoliasearch/lite';
import { InstantSearch, SearchBox, useInfiniteHits, RefinementList, SortBy, Configure, useStats } from 'react-instantsearch';
import styled from 'styled-components';
import ProductBox from './product-box';
import SuspenseBoundary from '../components/organisms/suspense-boundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Button from '../components/atoms/button';

const queryClient = new QueryClient();


const algoliaClient = algoliasearch('PY4BD5F6JD', '3db26bfb121e0b734a2c0dbaa63f7568');



export default function FullPageSearchSearchWidget(props) {
  const [showFilters, setShowFilters] = React.useState(false);
  const { term } = props;

  if (!term) {
    return (<div>No search term provided</div>);
  }

  const searchClient = {
    ...algoliaClient,
    search(requests) {
      if (requests.every(({ params }) => !params.query)) {
        return Promise.resolve({
          results: requests.map(() => ({
            hits: [],
            nbHits: 0,
            nbPages: 0,
            page: 0,
            processingTimeMS: 0,
            hitsPerPage: 0,
            exhaustiveNbHits: false,
            query: '',
            params: '',
          })),
        });
      }
      const search = algoliaClient.search(requests);
      return search;
    },
  };
    
  return (
    <QueryClientProvider client={queryClient}>
      <Container>
        {/* <InstantSearch searchClient={searchClient} indexName="gnedi_products__price__desc" > */}
        <InstantSearch searchClient={searchClient} indexName="gnedi_products" initialUiState={{gnedi_products: {query: term}}}>
          <Configure
            hitsPerPage={9}
            query={term}
          />  
          <SearchBoxContainer>
            <SearchBox resetIconComponent={() => null} />
          </SearchBoxContainer>
          <Button
            variant="secondary"
            fullwidth
            className="show-filters"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Filter Results"}
          </Button>
          <main>
            <SideBar showFilters={showFilters} />
            <Content term={term} />
          </main>
        </InstantSearch>
      </Container>
    </QueryClientProvider>
  );
}

function SideBar(props) {
  const { showFilters } = props;
  const { hits } = useInfiniteHits();
  const equipmentTypeRef = useRef(null);

  // add button class to show more button so it looks like our primary button
  React.useLayoutEffect(() => {
    if (equipmentTypeRef.current !== null) {
      const showMoreButton = equipmentTypeRef.current.querySelector('.ais-RefinementList-showMore')
      if (showMoreButton) {
        showMoreButton.classList.add("btnSec")
        showMoreButton.classList.add("button--small")
      }
    }
  })
  return (
    <SidebarContainer className={showFilters ? "is-shown" : null} ref={equipmentTypeRef}>
      <h2>Filter</h2>
      {hits.length > 0 &&
      <>
        <h3>Equipment Type</h3>
        <StyledRefinementList attribute="EquipmentType" limit={10} showMore={true} showMoreLimit={100} sortBy={["isRefined", "name:asc", "count:desc"]} /> 
        <h3>Brand</h3>
        <StyledRefinementList attribute="Brand" sortBy={["isRefined", "name:asc", "count:desc"]} />
        <h3>Category</h3>
        <StyledRefinementList attribute="Category" sortBy={["isRefined", "name:asc", "count:desc"]} />
      </>
      }
    </SidebarContainer>
  );
}

function Content(props) {
  const { term, showFilters } = props;
  return (
    <SuspenseBoundary fallback={<div>Loading...</div>}>
      <HitsContainer className={showFilters ? "" : "is-hidden"}>
        <h1 className="u-color--dark-green">Search Results for "{term}"</h1>  
        <header className="sort-container">
          <SortBy
            defaultRefinement="gnedi_products"
            items={[
              { value: 'gnedi_products', label: 'Most Popular' },
              { value: 'gnedi_products__price__desc', label: 'Price: High to Low' },
              { value: 'gnedi_products__price__asc', label: 'Price: Low to High' }
            ]}
            />
          <strong>Sort</strong>
          <Stats />
        </header>
        <InfiniteHits />
      </HitsContainer>
    </SuspenseBoundary>
  );
}

function InfiniteHits(props) {
  const { hits, isLastPage, showMore } = useInfiniteHits(props);
  const sentinelRef = React.useRef(null);
  React.useEffect(() => {
    if (sentinelRef.current !== null) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLastPage) {
            // Load more hits
            showMore()
          }
        });
      });

      observer.observe(sentinelRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [isLastPage, showMore]);

  if (!hits?.length) {
    return 'No products were found that matched your criteria.';
  }
 
  return (
    <div className="ais-InfiniteHits">
      <ul className="ais-InfiniteHits-list">
        {hits.map((hit) => (
          <li key={hit.objectID} className="ais-InfiniteHits-item">
            <ProductBox hit={hit} />
          </li>
        ))}
        <li ref={sentinelRef} aria-hidden="true" />
      </ul>
    </div>
  )
}

function Stats() {
  const { nbHits } = useStats();
  return (
    <StatsContainer>
      Results ({nbHits})
    </StatsContainer>
  )
}

const HitsContainer = styled.div`
  width: 100%;

  .ais-InfiniteHits-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(225px, 1fr));
    gap: 20px;
  }


  .sort-container {
    display: flex;
    flex-flow: row-reverse nowrap;
    gap: 10px;
    margin: 20px 0 10px;
    padding: 10px;
    background: var(--color--light-grey);
  }
`;

const SearchBoxContainer = styled.div`
  display: none;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-flow: column;
  gap: 20px;
  margin: 0 0 40px;
  text-align: left;
  max-width: 1200px;
  padding: 2px 0 0;

  header {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 20px;
  }

  main {
    display: flex;
    flex-flow: row nowrap;
    gap: 20px;
    position: relative;
  }

  .show-filters {
    margin: 20px 0 0;
    @media (min-width: 1025px) {
      display: none;
    }
  }
`;

const StyledRefinementList = styled(RefinementList)`
  width: 100%;
  display: flex;
  flex-flow: row wrap;
  gap: 10px;
  justify-content:flex-start;
  margin:10px 0 40px;

  .ais-RefinementList-list {
    width: 100%;
  }

  .ais-RefinementList-item {
    border-bottom: 1px solid #ccc;
    padding: 10px 0;
    width: 100%;
    flex: 1;
  }

  .ais-RefinementList-label {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    /* padding: 5px 0; */
    gap: 10px;
    cursor: pointer;
  }

  .ais-RefinementList-count {
    background-color: #eee;
    border-radius: 10px;
    padding: 5px;
    line-height: 1;
    color: black
  }

  .ais-RefinementList-showMore--disabled {
    display: none;
  }

  .ais-RefinementList-showMore {

  }
`

const SidebarContainer = styled.aside`
  width: 275px;
  position: absolute;
  transform: translateX(-275px);
  transition: transform 0.3s ease-in-out;
  background: white;
  z-index: 100;
  top: 0;
  left: 0;

  @media (min-width: 1025px) {
    position: unset;
    transform: translateX(0);
  }
  
  &.is-shown {
    transform: translateX(0);
    position: relative;
  }
  padding: 0 20px 0 0;
  border-right: 1px solid var(--color--light-grey);
  h2 {
    padding: 10px 0;
    margin: 0 0 10px;
    background: var(--color--light-grey);
    color: var(--color--dark-green);
    text-align: center;
    width: 250px;
    font: normal 22px 'eurostile';
  }

  h3 {
    color: var(--color--dark-green);
    font: bold 18px 'eurostile';
  }
`

const StatsContainer = styled.span`
  margin-right: auto;
`;
